import {createAction} from '@reduxjs/toolkit'

import {ApiError, ApiResponse, NetworkError} from '@shared/helpers/api'
import {
    EstimateNaverFinancialPaymentRequest,
    EstimateNaverFinancialPaymentResponse,
    EstimateRewardBasedOnCardParam,
    EstimateRewardBasedOnCardResult,
} from '@shared/schemas/payment'

import getApi, {createApiAsyncThunk, createWrapper} from '@web/helpers/api'
import {AccountState} from '@web/slices/account/slice'
import {cardEasyPayInfoSelector, CardState} from '@web/slices/card/slice'
import {ChargePointState} from '@web/slices/chargePoint/slice'
import {commonSelector, CommonState} from '@web/slices/common/slice'
import {couponSelector, CouponState} from '@web/slices/coupon/slice'
import {OrderTotalState} from '@web/slices/orderTotal/slice'
import {PayLaterState} from '@web/slices/payLater/slice'
import {PaymentMethodState} from '@web/slices/paymentMethod/slice'
import {PointPaymentState} from '@web/slices/pointPayment/slice'
import {CashTradeReasonClass, RewardItem, RewardType} from '@web/slices/reward/slice'
import {RewardPointState} from '@web/slices/rewardPoint/slice'

import {
    UpdatePaymentBenefitInfoPayload,
    UpdatePaymentBenefitInfoParam,
    UpdatePayMoneyRewardPayload,
    UpdatePayMoneyRewardParam,
} from './interface'

type RejectValue =
    | {
          exceptionCode: string
          message: string
          returnUrl?: string
          reserveId: string
      }
    | GatewayError

/**
 * 요청이 여러번 이루어질 수 있고, 가장 마지막에 요청된 응답값인지를 확인하기 위한 값
 * debounce 적용되어있지만 약간의 차이로 앞 요청이 먼저 올 수 있음
 */
let latestEstimateRequestInfo = null

type GatewayError = NetworkError | ApiError

const paymentEstimateCache = new Map<string, EstimateNaverFinancialPaymentResponse>()
const cardEstimateCache = new Map<string, EstimateRewardBasedOnCardResult>()

/** 결제형 > 예상 적립포인트 조회 */
export const updatePaymentBenefitInfo = createApiAsyncThunk<
    UpdatePaymentBenefitInfoPayload,
    UpdatePaymentBenefitInfoParam,
    {
        state: {
            account: AccountState
            card: CardState
            chargePoint: ChargePointState
            coupon: CouponState
            common: CommonState
            payLater: PayLaterState
            paymentMethod: PaymentMethodState
            pointPayment: PointPaymentState
            orderTotal: OrderTotalState
            rewardPoint: RewardPointState
        }
        rejectValue: RejectValue
    }
>(
    'benefitInfo/updatePaymentBenefitInfo',
    async (
        {
            reservedId,
            bankAmount,
            cardAmount,
            cardId,
            payMoneyAmount,
            payPointAmount,
            isUsingNaverAccountBankCode,
            couponId,
            selectedRechargePayMethod,
            payable,
        },
        {rejectWithValue},
    ) => {
        const requestInfo: EstimateNaverFinancialPaymentRequest = {
            reserveId: reservedId,
            bankAmount: bankAmount.toString(),
            cardAmount: cardAmount.toString(),
            cardId,
            payMoneyAmount: payMoneyAmount.toString(),
            isUsingNaverAccountBankCode: isUsingNaverAccountBankCode.toString(),
            payPointAmount: payPointAmount.toString(),
            ...(couponId && {publicationNumber: couponId}),
        }
        latestEstimateRequestInfo = requestInfo

        const cacheKey = JSON.stringify(requestInfo, Object.keys(requestInfo).sort())

        const cachedResult = paymentEstimateCache.get(cacheKey)

        const response =
            cachedResult ??
            (await (async () => {
                const api = getApi('/orderApi/payment')
                const apiResponse = await createWrapper(
                    api.get<
                        EstimateNaverFinancialPaymentRequest,
                        ApiResponse<ApigwResponseBase<EstimateNaverFinancialPaymentResponse>>
                    >('/naverFinancialPayment/orderSheet/estimate', {params: requestInfo}),
                )

                // 결과 깊은 복사 후 캐시에 저장
                const resultCopy = {...apiResponse}
                paymentEstimateCache.set(cacheKey, resultCopy)

                return apiResponse
            })())

        if (latestEstimateRequestInfo !== requestInfo) {
            return rejectWithValue({
                exceptionCode: 'Outdated',
                message: 'Outdated request',
                reserveId: reservedId,
            })
        }

        /**
         * 단순 머니 적립 표시용인 경우 CHARGE_USE_ADD_ACM 케이스 사용
         * @see https://oss.fin.navercorp.com/Product-Planning/Online-Payment_Plan/issues/653#issuecomment-2187396
         */
        const hasPayMoneyAccumulation = response.accumulationEstimations.some(
            (item) => item.accumulationType === CashTradeReasonClass.CHARGE_USE_ADD_ACM,
        )
        const accumulationEstimations = response.accumulationEstimations.reduce<RewardItem[]>((result, item) => {
            // 이벤트 적립
            if (item.accumulationType === CashTradeReasonClass.PROMOTION_ACM) {
                result.push({
                    type: RewardType.EVENT,
                    /**
                     * 혜택 API에서 적립명 그대로 응답함
                     * 하지만 혜택 BE에서는 다국어 대응 예정이 없음
                     * 추후 다국어 대응이 필요할 경우 혜택 BE와 협의 필요
                     *
                     * accumulationType으로 적립유형을 구분하지 못함
                     * (=> 통장 적립금에서 어떤 통장에 의한 적립인지 알 수 없음)
                     *
                     *  기본적립: BUY_ACCUMULATE
                     *  판매자추가 구매적립: MERCHANT_BUY_ACCUMULATE
                     *  머니 결제적립: CHARGE_USE_ADD_ACM
                     *  통장 적립(⭐️ 하나/미래에셋/우리/네통 구분 못함): NAVER_BANKBOOK_ADD_ACM
                     *  멤버십 추가적립: MEMBERSHIP_ACM
                     */
                    title: item.name,
                    amount: item.accumulationAmount,
                    hasMembershipBenefit: false,
                })
            } else {
                // 이벤트 적립이 아니면 모두 구매 적립으로 처리
                result.push({
                    type: RewardType.PURCHASE,
                    title: item.name,
                    amount: item.accumulationAmount,
                    hasMembershipBenefit: item.accumulationType === CashTradeReasonClass.MEMBERSHIP_ACM,
                })
            }

            return result
        }, [])
        const totalAccumulateEstimation = accumulationEstimations.reduce((acc, item) => acc + item.amount, 0)

        const {accumulationRate} = response.payMoneyMethodEstimation

        const expectedAccumulateRate = !hasPayMoneyAccumulation ? 0 : accumulationRate

        return {
            reserveId: response.reserveId ?? '',
            expectedAccumulateRateForRecharging: selectedRechargePayMethod ? expectedAccumulateRate : 0,
            accumulationEstimations: totalAccumulateEstimation <= 0 || !payable ? [] : accumulationEstimations,
        }
    },
)

/** 구매 적립 정보 초기화 */
export const resetPurchaseReward = createAction('benefitInfo/resetPaymentBenefitReward')

/** 페이머니 선택시 최대 적립액 */
export const updatePayMoneyReward = createApiAsyncThunk<
    UpdatePayMoneyRewardPayload,
    UpdatePayMoneyRewardParam,
    {
        state: {account: AccountState; coupon: CouponState; common: CommonState}
        rejectValue: RejectValue
    }
>(
    'benefitInfo/updatePayMoneyReward',
    async (
        {reserveId, bankAmount, cardAmount, cardId, payMoneyAmount, isUsingNaverAccountBankCode, payPointAmount},
        {getState},
    ) => {
        const state = getState()
        const coupons = couponSelector(state)
        const {seqID} = commonSelector(state)

        const publicationNumber = coupons?.applying?.selected.items[seqID]?.immediateDiscount?.[0]?.couponId ?? ''
        const isCouponApplied = !!publicationNumber

        const requestInfo: EstimateNaverFinancialPaymentRequest = {
            reserveId,
            bankAmount: bankAmount.toString(),
            cardAmount: cardAmount.toString(),
            cardId,
            payMoneyAmount: payMoneyAmount.toString(),
            isUsingNaverAccountBankCode: isUsingNaverAccountBankCode.toString(),
            payPointAmount: payPointAmount.toString(),
            publicationNumber: isCouponApplied ? publicationNumber : undefined,
        }

        const cacheKey = JSON.stringify(requestInfo, Object.keys(requestInfo).sort())

        const cachedResult = paymentEstimateCache.get(cacheKey)

        const response =
            cachedResult ??
            (await (async () => {
                const api = getApi('/orderApi/payment')
                const apiResponse = await createWrapper(
                    api.get<
                        EstimateNaverFinancialPaymentRequest,
                        ApiResponse<ApigwResponseBase<EstimateNaverFinancialPaymentResponse>>
                    >('/naverFinancialPayment/orderSheet/estimate', {params: requestInfo}),
                )

                // 결과 깊은 복사 후 캐시에 저장
                const resultCopy = {...apiResponse}
                paymentEstimateCache.set(cacheKey, resultCopy)

                return apiResponse
            })())

        const {accumulationEstimations} = response

        const totalExpectedRewardAmount = accumulationEstimations.reduce((acc, estimation) => {
            return acc + estimation.accumulationAmount
        }, 0)
        const expectedChargePoint = accumulationEstimations.reduce((acc, estimation) => {
            return (
                acc +
                (estimation.accumulationType === CashTradeReasonClass.CHARGE_USE_ADD_ACM
                    ? estimation.accumulationAmount
                    : 0)
            )
        }, 0)

        return {
            reserveId: response.reserveId ?? '',
            totalExpectedRewardAmount,
            expectedChargePoint,
        }
    },
)

let latestRequestId = null
/**
 * 인증형 단건 > 견적(적립) 조회
 */
export const estimateRewardBasedOnCard = createApiAsyncThunk<
    EstimateRewardBasedOnCardResult,
    EstimateRewardBasedOnCardParam,
    {
        state: {
            card: CardState
        }
        rejectValue: RejectValue
    }
>('benefitInfo/estimateRewardBasedOnCard', async (param, {getState, rejectWithValue, requestId}) => {
    latestRequestId = requestId

    const {
        selected: {info},
    } = cardEasyPayInfoSelector(getState())

    // 선택된 카드가 결제 불가능한 경우 reject 처리
    if (!info || !info.payService.payable || !info.payService.supportedMerchant) {
        return rejectWithValue({
            exceptionCode: 'NotPayable',
            message: '선택된 카드가 결제 불가능 하거나, 해당 가맹점에서 지원되지 않습니다.',
            reserveId: param.reserveId,
        })
    }

    const cacheKey = JSON.stringify({cardAmount: param.cardAmount, cardId: param.cardId})

    const cachedResult = cardEstimateCache.get(cacheKey)

    const response =
        cachedResult ??
        (await (async () => {
            const apiResponse = await createWrapper(
                getApi('/orderApi/payment').get<
                    EstimateRewardBasedOnCardParam,
                    ApiResponse<ApigwResponseBase<EstimateRewardBasedOnCardResult>>
                >('/partners/estimate', {params: param}),
            )

            const resultCopy = {...apiResponse}
            cardEstimateCache.set(cacheKey, resultCopy)

            return apiResponse
        })())

    // 최근에 발생한 요청의 응답이 아니라면 이전 요청에 대한 응답이므로 reject 처리
    if (latestRequestId !== requestId) {
        return rejectWithValue({
            exceptionCode: 'Outdated',
            message: '최근에 발생한 요청에 대한 응답이 아닙니다.',
            reserveId: param.reserveId,
        })
    }

    return response
})
